import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/office-equipment-storage.jpg'),
    alt: 'office equipment storage page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'office equipment storage page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Office Equipment <span>Storage</span> <span>For Business Needs</span>',
  description:
    "As your business grows, so does your need for space. ShipAndStorage provides tailored office equipment storage solutions for relocation, renovation, or when you're outgrowing your current office. ",
  image: {
    url: '/images/v2/pages/main-pages/office-equipment-storage/hero-image.jpg',
    alt: 'office equipment storage page'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Reserve Your Space' }
}

export const helperSectionContent = {
  title: 'Office Equipment Storage for Business Needs',
  description: 'Moving or upgrading your office can be stressful, but ShipAndStorage makes the process smoother.',

  articles: [
    {
      content: [
        {
          id: 1,
          points: [
            '<strong>Pre-Move Preparation</strong></br> We provide secure storage to hold your equipment during the transition.'
          ]
        },
        {
          id: 2,
          points: [
            '<strong>Safe and Efficient Transport</strong></br> Our pickup and delivery services ensure your items are moved safely.'
          ]
        },
        {
          id: 3,
          points: [
            '<strong>Flexible Storage Options</strong></br> Choose a plan that fits your timeline, whether it’s for a few weeks or longer.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/office-equipment-storage-for-business-needs.svg',
        alt: 'Office Equipment Storage for Business Needs'
      },
      link: { href: PUBLIC_MAIN_ROUTES.HOW_SHIP_AND_STORAGE_WORKS.href, text: 'Store with Confidence' }
    }
  ]
}

export const safeAndSecureOfficeEquipmentStorageSectionContent = {
  title: 'Safe and Secure Office Equipment Storage',

  articles: [
    {
      content: [
        {
          id: 1,
          description:
            'Our storage facilities are equipped with advanced security systems, including 24/7 surveillance, restricted access, and climate control to ensure your office equipment stays in pristine condition. Whether you’re relocating, renovating, or managing excess inventory, we provide a safe and reliable storage solution tailored to your business needs.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/safe-and-secure-office-equipment-storage.jpg',
        alt: 'Office Equipment Storage for Business Needs'
      }
    }
  ]
}

export const serviceTipsSectionContent = {
  title: 'Why Choose Us for Office Equipment Storage?',
  description:
    'At ShipAndStorage, we understand that businesses need reliable, efficient, and cost-effective solutions.',

  cards: [
    {
      id: 1,
      title: 'Enhanced Security Measures',
      description:
        'Your equipment is safe with us. We provide 24/7 surveillance, digital access controls, and individually monitored units to ensure complete peace of mind.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/enhanced-security-measures.svg',
        alt: 'Enhanced Security Measures'
      }
    },
    {
      id: 2,
      title: 'Flexible Storage Options',
      description:
        'From short-term projects to long-term plans, our storage solutions are adaptable to your timeline without requiring long-term commitments.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/flexible-storage-options.svg',
        alt: 'Flexible Storage Options'
      }
    },
    {
      id: 3,
      title: 'Wide Range of Unit Sizes',
      description:
        "Only pay for what you need. Whether you're storing a few items or an entire office’s worth of equipment, we have the perfect unit size for you.",
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/wide-range-of-unit-sizes.svg',
        alt: 'Wide Range of Unit Sizes'
      }
    },
    {
      id: 4,
      title: 'Pickup and Delivery Services',
      description:
        'Save time and effort. We offer hassle-free pickup and delivery, ensuring your equipment is transported safely and efficiently.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/pickup-and-delivery-services.svg',
        alt: 'Pickup and Delivery Services'
      }
    },
    {
      id: 5,
      title: 'Climate-Controlled Facilities',
      description:
        'Sensitive items like electronics, furniture, and documents are protected in our temperature-regulated storage units.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/climate-controlled-facilities.svg',
        alt: 'Climate-Controlled Facilities'
      }
    },
    {
      id: 6,
      title: 'Transparent & Affordable Pricing',
      description:
        'No hidden costs. Our competitive rates are designed to fit your budget, providing maximum value for your storage investment.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/transparent-affordable-pricing.svg',
        alt: 'Transparent & Affordable Pricing'
      }
    }
  ]
}

export const whyWeSectionContent = {
  title: 'What Can You Store?',
  description: 'ShipAndStorage offers specialized storage for a wide variety of office equipment.',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Find the Right Storage Unit' },

  cards: [
    {
      id: 1,
      title: 'Office Furniture',
      description:
        'Store desks, chairs, conference tables, filing cabinets, and shelving units with complete ease and efficiency.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/office-furniture.svg',
        alt: 'Office Furniture'
      }
    },
    {
      id: 2,
      title: 'Electronics and IT Equipment',
      description:
        'Keep computers, monitors, printers, scanners, and servers in optimal condition with our climate-controlled units.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/electronics-and-it-equipment.svg',
        alt: 'Electronics and IT Equipment'
      }
    },
    {
      id: 3,
      title: 'Documents and Archives',
      description:
        'Securely store important files, records, and archives with options for digital retrieval and organization.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/documents-and-archives.svg',
        alt: 'Documents and Archives'
      }
    },
    {
      id: 4,
      title: 'Specialty Office Supplies',
      description:
        'Store promotional materials, trade show equipment, and seasonal decor without cluttering your workspace.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/specialty-office-supplies.svg',
        alt: 'Specialty Office Supplies'
      }
    }
  ]
}

export const benefitsOfOfficeEquipmentStorageSectionContent = {
  title: 'Benefits of Office Equipment Storage',
  description:
    'Using professional office equipment storage offers a range of benefits that can help optimize your business operations:',

  cards: [
    {
      id: 1,
      title: 'Cost-Effective Space Management',
      description:
        'Free up valuable office space and avoid the expense of relocating by utilizing affordable storage solutions.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/cost-effective-space-management.svg',
        alt: 'Cost-Effective Space Management'
      }
    },
    {
      id: 2,
      title: 'Reduced Clutter',
      description: 'A tidy, organized workspace leads to a more productive environment and boosts employee morale.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/reduced-clutter.svg',
        alt: 'Reduced Clutter'
      }
    },
    {
      id: 3,
      title: 'Streamlined Relocation & Renovation',
      description:
        'Securely store your office equipment during transitions, moves, or renovations for a hassle-free process.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/streamlined-relocation-renovation.svg',
        alt: 'Streamlined Relocation & Renovation'
      }
    },
    {
      id: 4,
      title: 'Improved Operational Efficiency',
      description:
        'Keep essential equipment accessible without overcrowding your workspace, improving workflow and efficiency.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/improved-operational-efficiency.svg',
        alt: 'Improved Operational Efficiency'
      }
    },
    {
      id: 5,
      title: 'Enhanced Security',
      description: 'Store valuable office items in a secure facility, protecting them from theft, damage, or loss.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/enhanced-security.svg',
        alt: 'Enhanced Security'
      }
    },
    {
      id: 6,
      title: 'Scalability',
      description:
        'Adapt your storage solutions as your business grows, ensuring flexible options to meet your evolving needs.',
      image: {
        src: '/images/v2/pages/main-pages/office-equipment-storage/scalability.svg',
        alt: 'Scalability'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Safe Office Equipment Storage for Businesses',
  description:
    'Store office equipment safely and retrieve it when needed. Ideal for businesses of all sizes with flexible storage options.',
  image: {
    src: '/images/v2/pages/main-pages/office-equipment-storage/promotional-banner-image.svg',
    alt: 'Safe Office Equipment Storage for Businesses'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Request Free Quote' }
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'What types of office equipment can I store with ShipAndStorage?',
      answer:
        'You can store furniture, electronics, IT equipment, documents, promotional materials, and specialty items like trade show supplies.'
    },
    {
      id: 2,
      question: 'Are the storage units climate-controlled?',
      answer:
        'Yes, we offer temperature-regulated storage units to protect sensitive items like electronics and documents.'
    },
    {
      id: 3,
      question: 'Can you help with pickup and delivery?',
      answer:
        'Absolutely! We provide convenient pickup and delivery services to transport your items safely and efficiently.'
    },
    {
      id: 4,
      question: 'Is there a minimum storage duration?',
      answer: 'No, we offer flexible storage plans, whether you need space for a few weeks or long-term solutions.'
    },
    {
      id: 5,
      question: 'How secure are the storage facilities?',
      answer:
        'Our facilities feature 24/7 surveillance, restricted access, and individually monitored units to ensure maximum security for your equipment.'
    }
  ]
}
